import axios from "axios";
import * as Sentry from "@sentry/react";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api/v1",
  timeout: 30_000,
  // Send the session cookie on every request. Login sets `rp_session`,
  // and the browser only returns it on cross-origin XHR when
  // withCredentials is set.
  withCredentials: true,
});

// A 401 means the session is gone or expired. AuthProvider registers a
// handler here that flips the app back to the login screen. Kept as a
// module-level hook (not a React import) so the axios instance stays
// framework-agnostic.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

// Capture the X-Request-Id the backend mints for every response and push
// it onto the Sentry scope. If anything explodes after this API call,
// the resulting Sentry event carries the backend's correlation id so
// frontend↔backend issues link up. Cheap, always on, no PII.
api.interceptors.response.use(
  (response) => {
    const requestId = response.headers["x-request-id"];
    if (requestId) {
      Sentry.getCurrentScope().setTag("backend_request_id", requestId);
    }
    return response;
  },
  (error) => {
    const requestId = error?.response?.headers?.["x-request-id"];
    if (requestId) {
      Sentry.getCurrentScope().setTag("backend_request_id", requestId);
    }
    // Drop to the login screen on 401 — but never for the auth endpoints
    // themselves, or a failed login / me-check would recurse.
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? "";
    if (status === 401 && onUnauthorized && !url.includes("/auth/")) {
      onUnauthorized();
    }
    return Promise.reject(error);
  },
);

/**
 * Build an absolute, browser-navigable URL for a backend path that the
 * API returns as root-relative (e.g. "/api/v1/freshservice/.../open").
 *
 * Why this exists: axios sends API calls to `baseURL` (a full backend
 * URL in prod via VITE_API_BASE_URL). But a root-relative href in an
 * <a> tag resolves against the FRONTEND origin, which has no /api/*
 * rewrite — so the SPA fallback turns it into index.html and the
 * browser sees a 404. For links the browser navigates to directly
 * (file downloads, redirects), we must point at the API origin, not a
 * relative path.
 *
 * - Prod: baseURL = "https://host/api/v1" → origin "https://host" +
 *   the path = a full URL that actually hits the backend.
 * - Dev:  baseURL = "/api/v1" (relative) → returns the path unchanged,
 *   which works because the dev server proxies same-origin.
 */
export function apiFileHref(pathFromRoot: string): string {
  const base = api.defaults.baseURL || "/api/v1";
  if (/^https?:\/\//i.test(base)) {
    const origin = base.replace(/\/api\/v1\/?$/, "");
    return origin + pathFromRoot;
  }
  return pathFromRoot;
}

export default api;
